/**
 * Market Registry
 *
 * Central registry for market-specific behaviors.
 * Markets can be registered to override default behaviors.
 */

import type { CheckoutConfig, ModuleActivation } from '@/lib/configuration/types';
import { getPaymentLimit, isModuleActive } from '@/lib/configuration/types';
import { createMarketBehavior } from './defaults';
import type { MarketBehavior, MarketContext } from './types';

/**
 * Market Registry Class
 * Manages market-specific configurations and behaviors
 */
class MarketRegistry {
  private markets: Map<string, Partial<MarketBehavior>> = new Map();

  /**
   * Register a market-specific behavior override
   */
  register(countryCode: string, behavior: Partial<MarketBehavior>): void {
    const normalized = countryCode.toUpperCase();
    this.markets.set(normalized, behavior);
  }

  /**
   * Register multiple markets at once
   */
  registerMany(markets: Record<string, Partial<MarketBehavior>>): void {
    for (const [countryCode, behavior] of Object.entries(markets)) {
      this.register(countryCode, behavior);
    }
  }

  /**
   * Get market behavior for a country
   * Falls back to default if not registered
   */
  get(countryCode: string): MarketBehavior {
    const normalized = countryCode.toUpperCase();
    const override = this.markets.get(normalized);

    if (override) {
      return createMarketBehavior(normalized, override);
    }

    // Return default with country-specific basics
    return createMarketBehavior(normalized, {});
  }

  /**
   * Check if a market has custom behavior
   */
  has(countryCode: string): boolean {
    return this.markets.has(countryCode.toUpperCase());
  }

  /**
   * Get all registered market codes
   */
  getRegisteredMarkets(): string[] {
    return Array.from(this.markets.keys());
  }

  /**
   * Create a market context with config data
   */
  createContext(
    countryCode: string,
    checkoutConfig: CheckoutConfig | null,
    moduleActivation: ModuleActivation | null
  ): MarketContext {
    const behavior = this.get(countryCode);

    return {
      countryCode: countryCode.toUpperCase(),
      behavior,
      checkoutConfig,
      moduleActivation,
      isModuleActive: (module: string) => isModuleActive(moduleActivation, module as never),
      getPaymentLimit: (method: string) => getPaymentLimit(checkoutConfig, method as never),
    };
  }

  /**
   * Clear all registered markets (useful for testing)
   */
  clear(): void {
    this.markets.clear();
  }
}

/**
 * Global market registry instance
 */
export const marketRegistry = new MarketRegistry();

/**
 * Get market behavior (convenience function)
 */
export function getMarketBehavior(countryCode: string): MarketBehavior {
  return marketRegistry.get(countryCode);
}

/**
 * Create market context (convenience function)
 */
export function createMarketContext(
  countryCode: string,
  checkoutConfig: CheckoutConfig | null,
  moduleActivation: ModuleActivation | null
): MarketContext {
  return marketRegistry.createContext(countryCode, checkoutConfig, moduleActivation);
}

// Re-export for convenience
export { createMarketBehavior, defaultMarketBehavior } from './defaults';
